import { useMemo } from 'react';
import { ToolLink } from '../types/app';
import { useAppLinks as useTranslatedAppLinks } from '../constants/app';
import useInit from '../queries/user/useInit';
import { useLocalStorage } from './useLocalStorage';
import { Eyeglasses } from '@phosphor-icons/react';

function useAppLinks() {
  const { data: initData, isLoading, isPlaceholderData } = useInit();
  const [bookmarks] = useLocalStorage<string[]>('bookmarks', []);
  const translatedToolLinks = useTranslatedAppLinks();

  const appLinks = useMemo(() => {
    if (isLoading || isPlaceholderData) {
      return {
        tools: [...translatedToolLinks.map((link, index) => ({
          ...link,
          priority: index,
          active: false,
          requiresLogin: false,
          usesCredits: false,
          isNew: false,
          isPilot: false,
          topText: '',
        }))] as ToolLink[],
        features: [],
      };
    }

    const serverToolLinks: ToolLink[] | undefined = initData?.tools.map(tool => {
      let matchedInitialLink = translatedToolLinks.find(link => link.id === tool.id.toString());
      if (matchedInitialLink) {
        return ({
          icon: Eyeglasses, // Default icon if not found
          ...matchedInitialLink,
          priority: tool.priority,
          active: tool.active,
          requiresLogin: tool.requiresLogin,
          usesCredits: tool.usesCredits,
          isNew: tool.isNew,
          isPilot: tool.isPilot,
          topText: tool.topText,
          comingSoon: tool.comingSoon,
        });
      } else {
        console.warn("No matching applet link found in init. Tool display may be incorrect")
        return ({
          title: tool?.name,
          description: tool.topText,
          href: `/${tool?.name.toLowerCase().replace(/\s+/g, '-')}`,
          id: tool.id.toString(),
          priority: tool.priority,
          active: tool.active,
          requiresLogin: tool.requiresLogin,
          usesCredits: tool.usesCredits,
          isNew: tool.isNew,
          isPilot: tool.isPilot,
          topText: tool.topText,
          comingSoon: tool.comingSoon,
          icon: Eyeglasses, // Default icon if not found
        });
      }
    });

    const combinedLinks = [...(serverToolLinks || [])];

    combinedLinks.sort((a, b) => {
      const aBookmarked = bookmarks.includes(a.id);
      const bBookmarked = bookmarks.includes(b.id);

      if (aBookmarked && !bBookmarked) return -1;
      if (!aBookmarked && bBookmarked) return 1;

      return a.priority - b.priority;
    });


    return { tools: combinedLinks, features: initData?.features, isPilot: initData?.isPilot };
  }, [initData, isLoading, isPlaceholderData, bookmarks, translatedToolLinks]);

  return appLinks;
}

export default useAppLinks;